import React from 'react'
import i18next from 'i18next'
import hash from 'object-hash'
import { Trans } from 'react-i18next'
import { connect } from 'react-redux'
import { Card, Button } from 'react-bootstrap'
import ActionsDropdown from 'src/components/system_wide/actions_dropdown'
import SearchButton from 'src/components/system_wide/search_button'
import ResponsiveTable from 'src/components/system_wide/responsive_table'
import FilterGroup from 'src/components/system_wide/filter_group'
import CheckboxFilter from 'src/components/system_wide/checkbox_filter'
import ImagePreview from 'src/components/system_wide/image_preview'
import DocumentPreview from 'src/components/system_wide/document_preview'
import SelectAgentsModal from 'src/components/system_wide/select_agents_modal'
import {
  getMetadata,
  resetCurrentMetadata,
  resetMetadata,
  setCurrentMetadata,
  toggleShowForm,
} from 'src/store/metadata'
import { getAllUsers } from 'src/store/users'
import { getAllBrands } from 'src/store/brand'
import { parseISO, format } from 'date-fns'
import PhotoEvaluation from './photo_evaluation'
import AppointmentForm from 'src/components/appointments/appointment_form'
import { getConstants } from 'src/lib/constants'

const { t } = i18next

const mstp = state => {
  let { id: user_id, business_role_priority } = state.userInfo
  let { total, metadata, show_form, current_metadata } = state.metadata
  let { all_brands: brands } = state.brands
  let { all_users: users } = state.users
  return { user_id, business_role_priority, total, metadata, show_form, current_metadata, brands, users }
}

class Metadata extends React.Component {
  state = {
    show_modal: false,
    filter: '',
    filtered_brands: [],
    filtered_users: [],

    filters_hash: '',
    ready: false,
  }

  initialFiltersHash = ''
  tableRef = React.createRef()
  previewModalRef = React.createRef()
  selectAgentsModalRef = React.createRef()
  appointmentTypes = getConstants().appointment_types

  async componentDidMount() {
    await this.props.dispatch(getAllBrands())
    await this.props.dispatch(getAllUsers({ filtered_roles_priority: [4] }))
    this.setState(
      {
        filtered_brands: this.props.brands.map(b => b.id),
        filtered_users: this.props.users.map(u => u.id),
        ready: true,
      },
      () => {
        let { filtered_brands, filtered_users } = this.state
        filtered_brands.sort()
        filtered_users.sort()
        this.initialFiltersHash = hash({
          filtered_brands: filtered_brands,
          filtered_users: filtered_users,
        })
        this.updateFiltersHash()
        this.tableRef?.current?.refreshData()
      }
    )
  }

  updateFiltersHash = () => {
    let { filtered_brands, filtered_users } = this.state
    filtered_brands.sort()
    filtered_users.sort()
    this.setState({ filters_hash: hash({ filtered_brands, filtered_users }) })
  }

  actions = [
    {
      name: 'view_appointment',
      variant: 'outline-primary',
      icon: 'fas fa-eye',
      text: t('actions.view_appointment', 'Visualizza visita'),
      onClick: rowData => {
        this.props.dispatch(setCurrentMetadata({ ...rowData }))
        this.props.dispatch(toggleShowForm())
      },
    },
  ]

  actionsRenderer = ({ rowData }) => {
    return <ActionsDropdown rowData={rowData} actions={this.actions} />
  }
  imageRenderer = ({ rowData }) => {
    let { id, filename, name } = rowData
    let url = filename ? `/api/appointments/${id}/image` : ''

    return (
      <div className="d-flex justify-content-center align-items-center" style={{ width: '100%', height: 'auto' }}>
        <ImagePreview
          src={url}
          style={{ width: '6rem', height: '6rem' }}
          onClick={() =>
            this.previewModalRef.current.showModal({
              url,
              mime_type: 'image/*',
              title: name,
            })
          }
        />
      </div>
    )
  }

  renderDetails = rowData => {
    let { exif, appointment, created_by_name, created_at, description } = rowData
    let lat = null
    let lng = null
    if (appointment?.dealer_brand?.dealer) {
      lat = appointment.dealer_brand.dealer.lat
      lng = appointment.dealer_brand.dealer.lng
    }

    return (
      <Card className="shadow-sm mb-2 border-0 rounded-lg">
        <Card.Body className="text-center p-2">
          {this.imageRenderer({ rowData })}

          <h5 className="fw-bold text-primary d-flex align-items-center justify-content-center mb-1">
            <span>{appointment.code}</span>
            <PhotoEvaluation exif={exif} locationLat={lat || ''} locationLon={lng || ''} date={appointment.date} />
          </h5>

          {/* Creato da + Data su un'unica riga, ridotto ancora */}
          <p className="text-muted small mb-0">
            <strong>{created_by_name}</strong> - {format(parseISO(created_at), 'yyyy/MM/dd HH:mm')}
          </p>

          {/* Nome + Descrizione in una sola riga */}
          <p className="text-muted small mb-0">{description}</p>
        </Card.Body>

        <Card.Footer className="text-center bg-light p-1">
          <div className="d-flex justify-content-center flex-wrap gap-1">
            {this.actions
              .filter(a => (typeof a.visible === 'function' ? a.visible(rowData, a) : a?.visible ?? true))
              .map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant}
                  size="sm"
                  title={action.text}
                  disabled={action?.disabled ?? false}
                  onClick={() => action.onClick(rowData)}>
                  <i className={`${action.icon} me-0`}></i>
                </Button>
              ))}
          </div>
        </Card.Footer>
      </Card>
    )
  }

  columns = [
    {
      title: '',
      className: 'd-none d-sm-block text-center',
      style: { width: '6%', minWidth: '3rem' },
      data: this.actionsRenderer,
    },
    {
      title: '',
      className: 'd-none d-sm-block text-center',
      style: { width: '10rem' },
      data: this.imageRenderer,
    },
    {
      title: t('components.metadata.apointment_code', 'Codice visita'),
      className: 'd-none d-sm-block flex-fill',
      style: { width: '12%' },
      sortable: false,
      data: ({ rowData: { appointment } }) => appointment.code,
    },
    {
      title: t('components.metadata.description', 'Descrizione'),
      className: 'd-none d-sm-block flex-fill',
      style: { width: '12%' },
      sortable: false,
      data: 'description',
    },
    {
      title: t('components.metadata.exif', 'EXIF'),
      className: 'd-none d-sm-block flex-fill',
      style: { width: '5%' },
      sortable: false,
      data: ({ rowData: { exif, appointment } }) => {
        let { dealer_brand } = appointment
        let lat = null
        let lng = null
        if (dealer_brand?.dealer) {
          lat = dealer_brand.dealer.lat
          lng = dealer_brand.dealer.lng
        }
        return <PhotoEvaluation exif={exif} locationLat={lat} locationLon={lng} date={appointment.date} />
      },
    },
    {
      title: t('components.appointments.created_by', 'Creato da'),
      className: 'd-none d-sm-block flex-fill',
      style: { width: '12%' },
      sortable: true,
      data: 'created_by_name',
    },
    {
      title: t('components.appointments.created_at', 'Creato il'),
      className: 'd-none d-sm-block flex-fill',
      style: { width: '12%' },
      sortable: true,
      sortingKey: 'created_at',
      data: ({ rowData: { created_at } }) => format(parseISO(created_at), 'dd/MM/yyyy HH:mm:ss'),
    },
    {
      title: t('components.appointments.details', 'Dettagli'),
      className: 'd-block d-sm-none flex-fill',
      style: { width: '100%' },
      sortable: true,
      data: ({ rowData }) => this.renderDetails(rowData),
    },
  ]

  onFilterChange = filter => this.setState({ filter })

  onChangeFilter = name => values => {
    if (name === 'filtered_users') {
      values = values.map(user => user.id)
    }
    this.setState({ [name]: values }, () => {
      this.updateFiltersHash()
      this.tableRef?.current?.refreshData()
    })
  }

  getData = async (index = 0, chunkSize, sortData, filter) => {
    let { ready, filtered_brands, filtered_users } = this.state
    if (!ready) return
    await this.props.dispatch(
      getMetadata({ offset: index, limit: chunkSize, sortData, filter, filtered_brands, filtered_users })
    )
  }

  onToggleForm = () => {
    if (this.props.show_form === true) {
      this.props.dispatch(resetCurrentMetadata())
    }
    this.props.dispatch(toggleShowForm())
  }

  componentWillUnmount() {
    this.props.dispatch(resetMetadata())
  }

  onResetFilters = () => {
    this.setState(
      {
        filtered_brands: this.props.brands.map(b => b.id),
        filtered_users: this.props.users.map(u => u.id),
      },
      () => {
        this.tableRef?.current?.refreshData()
        this.updateFiltersHash()
      }
    )
  }

  render() {
    let { appointment } = this.props.current_metadata
    let { user_id, business_role_priority, brands, users, show_form, errors } = this.props
    let { filtered_brands, filtered_users, filters_hash } = this.state

    let title_appointment_form = `${t('components.appointments.view_appointment', 'Visualizza visita')}: ${
      this.appointmentTypes[appointment?.type]
    }`

    return (
      <div className="card card-body container-fluid flex-fill d-flex flex-column px-0 pb-0 pt-2 mb-0">
        <div className="d-flex flex-column flex-fill">
          <div className="toolbar px-2 flex-nowrap">
            <FilterGroup
              className="toolbar-item"
              showResetFilters={this.initialFiltersHash !== filters_hash}
              onResetFilters={this.onResetFilters}>
              <CheckboxFilter
                label={t('filter_brands', 'Brand')}
                items={brands}
                filteredItems={filtered_brands}
                onChangeFilter={this.onChangeFilter('filtered_brands')}
                showSearch={true}
              />
              {users.length > 0 && users[0].id !== user_id && (
                <Button
                  className="dropdown-toggle btn btn-primary"
                  onClick={() => this.selectAgentsModalRef.current.show({ selected_users: filtered_users })}>
                  <Trans i18nKey="filter_agents">Funzionari</Trans>
                </Button>
              )}
            </FilterGroup>
            <div className="toolbar-spacer"></div>
            <div className="flex-fill">
              <SearchButton
                value={this.state.filter}
                onChange={this.onFilterChange}
                disabled={show_form}
                className="float-md-end"
              />
            </div>
          </div>

          <ResponsiveTable
            ref={this.tableRef}
            className="flex-fill"
            data={this.props.metadata}
            getData={this.getData}
            totalRecords={this.props.total}
            columns={this.columns}
            showMenu={this.props.show_form}
            modalSize="xl"
            menuContent={AppointmentForm}
            menuContentProps={{
              title: title_appointment_form,
              business_role_priority: business_role_priority,
              appointmentFromProps: appointment,
              categories: appointment?.merchandise_category ? [appointment.merchandise_category] : [],
              errors: errors,
              closeForm: this.onToggleForm,
              readonly: true,
              forceUseModal: true,
            }}
            rowClasses={rowData => (rowData.enabled ? '' : `text-muted`)}
            filter={this.state.filter}
          />
        </div>

        <DocumentPreview ref={this.previewModalRef} />

        <SelectAgentsModal
          ref={this.selectAgentsModalRef}
          users={users}
          brands={brands}
          selected_user={filtered_users}
          onChange={this.onChangeFilter('filtered_users')}
          multiSelection={true}
        />
      </div>
    )
  }
}
export default connect(mstp)(Metadata)
